/**
 * Timezone Detection Manager Component
 * Manages timezone auto-detection and displays notifications to users
 */

import { useTimezoneDetectionNotification } from '@/hooks/useTimezoneDetectionNotification'
import { TimezoneDetectionToast } from '@/components/ui/timezone-detection-toast'
import { createPortal } from 'react-dom'

interface TimezoneDetectionManagerProps {
  /** Whether to show notifications as toast messages */
  showToast?: boolean
  /** Whether to show the custom notification component */
  showCustomNotification?: boolean
  /** Whether to auto-trigger detection on mount */
  autoTrigger?: boolean
  /** CSS class for positioning the notification */
  notificationClassName?: string
}

export function TimezoneDetectionManager({
  showToast = true,
  showCustomNotification = true,
  autoTrigger = true,
  notificationClassName = "fixed top-4 right-4 z-50"
}: TimezoneDetectionManagerProps) {
  const {
    showNotification,
    detectedTimezone,
    openSettings,
    dismissNotification
  } = useTimezoneDetectionNotification({
    autoTrigger,
    showToast,
    redirectToSettings: true
  })

  // Don't render if we're not showing custom notifications or there's nothing to show
  if (!showCustomNotification || !showNotification || !detectedTimezone) {
    return null
  }

  const notificationElement = (
    <TimezoneDetectionToast
      detectedTimezone={detectedTimezone}
      onChangeTimezone={openSettings}
      onDismiss={dismissNotification}
      className={notificationClassName}
    />
  )

  // Render as portal to ensure proper positioning
  return createPortal(notificationElement, document.body)
}

export default TimezoneDetectionManager